/** Bounds prompt-free wallet reads without retaining a stalled request for the next attempt. */
export const WALLET_READ_TIMEOUT_MS = 10_000;

export class WalletReadTimeoutError extends Error {
  constructor() {
    super('Your wallet did not respond to the wallet check. Open your wallet, then try again.');
    this.name = 'WalletReadTimeoutError';
  }
}

/**
 * Only use for reads. Timing out cannot cancel a wallet prompt, signature, or transaction.
 * A late read result is ignored; the caller must start a fresh read on an explicit retry.
 */
export async function waitForWalletRead<T>(request: Promise<T>, timeoutMs = WALLET_READ_TIMEOUT_MS): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      request,
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new WalletReadTimeoutError()), timeoutMs);
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

type WalletReadMethod =
  | 'eth_accounts'
  | 'eth_chainId'
  | 'eth_getBalance'
  | 'eth_estimateGas'
  | 'eth_getBlockByNumber'
  | 'eth_call'
  | 'eth_getCode'
  | 'eth_blockNumber'
  | 'eth_getLogs'
  | 'eth_getTransactionByHash'
  | 'eth_getTransactionCount'
  | 'eth_getTransactionReceipt'
  | 'wallet_getCapabilities'
  | 'wallet_getCallsStatus'
  | 'wallet_getSnaps';

export function requestWalletRead<T>(
  provider: { request: <R = unknown>(args: { method: string; params?: unknown }) => Promise<R> },
  args: { method: WalletReadMethod; params?: unknown },
  timeoutMs = WALLET_READ_TIMEOUT_MS,
): Promise<T> {
  return waitForWalletRead(
    Promise.resolve().then(() => provider.request<T>(args)),
    timeoutMs,
  );
}
